import React from "react";
import { ArrowRight, ChevronDown, Shield, Wrench, Cpu } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { resolveImageUrl } from "./ImageUploader";

export function Hero({ settings = {} }) {
  const { t } = useI18n();
  const heroImg = resolveImageUrl(settings.hero_image);
  const whatsappPhone = settings?.whatsapp_phone ? settings.whatsapp_phone.replace(/\D/g, "") : "";
  const whatsappUrl = settings?.whatsapp || (whatsappPhone ? `https://wa.me/${whatsappPhone}` : "");

  // Bant metinleri: admin'den geliyorsa kullan, yoksa varsayılan liste
  const DEFAULT_BAND = ["ISO 9001", "CE Certified", "TSE Approved", "EN 16005", "ISO 45001", "10 Yıl Garanti"];
  const bandItems = (settings.band_items && settings.band_items.length > 0)
    ? settings.band_items
    : (settings.band_items === null ? [] : DEFAULT_BAND); // null = admin silerek kapattı
  const showBand = bandItems.length > 0;

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative bg-[#0d1b2a] text-white overflow-hidden"
    >
      {/* Background image */}
      {heroImg && (
        <div className="absolute inset-0">
          <img src={heroImg} alt="YGS" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d1b2a] via-[#0d1b2a]/80 to-transparent" />
        </div>
      )}
      <div className="absolute inset-0 bg-grid opacity-40 pointer-events-none" />

      <div className="relative max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32">
        {/* Tek sütun — sağ kart kaldırıldı */}
        <div className="max-w-3xl">
          <div
            data-reveal
            className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-[#ffb703] bg-[#ffb703]/10 border border-[#ffb703]/30 rounded-full px-4 py-1.5"
            data-testid="hero-eyebrow"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#ffb703]" />
            {t.hero.eyebrow}
          </div>

          <h1
            data-reveal
            className="mt-6 text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.95] tracking-tight"
            style={{ fontFamily: "Chivo, Inter, sans-serif" }}
            data-testid="hero-title"
          >
            {t.hero.title1}
            <span className="block text-[#ffb703]">{t.hero.title2}</span>
          </h1>

          <p
            data-reveal
            className="mt-7 text-lg text-white/75 max-w-xl leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          <div data-reveal className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="btn-primary"
              data-testid="hero-cta-primary"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={18} />
            </a>
            <a
              href="#products"
              className="inline-flex items-center gap-2 text-white border-2 border-white/25 hover:border-[#ffb703] hover:text-[#ffb703] font-bold px-6 py-3 rounded-md transition-all duration-300"
              data-testid="hero-cta-secondary"
            >
              {t.hero.ctaSecondary}
              <ChevronDown size={18} />
            </a>
          </div>

          {/* Mini badges */}
          <div data-reveal className="mt-12 flex flex-wrap gap-3">
            {(settings.hero_badges && settings.hero_badges.length > 0
              ? settings.hero_badges
              : [
                  { icon: "Shield", label: "CE / TSE" },
                  { icon: "Wrench", label: "7/24 Servis" },
                  { icon: "Cpu",    label: "IoT Ready" },
                ]
            ).map((b, i) => {
              const icons = { Shield, Wrench, Cpu };
              const Icon = icons[b.icon] || Shield;
              return (
                <div
                  key={i}
                  className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-md px-3 py-2.5"
                >
                  <Icon size={16} className="text-[#ffb703]" />
                  <span className="text-xs font-semibold text-white/80">{b.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Kayan bant — sadece bandItems doluysa göster */}
      {showBand && (
        <div className="relative bg-[#ffb703] py-4 overflow-hidden">
          <div className="flex animate-marquee whitespace-nowrap">
            {/* Öğeleri 6 kez tekrarla — az öğede boşluk kalmasın */}
            {[...Array(6)].map((_, k) => (
              <div key={k} className="flex items-center gap-12 px-6 shrink-0">
                {bandItems.map((label, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0d1b2a]" />
                    <span className="text-sm font-bold uppercase tracking-wider text-[#0d1b2a]">
                      {label}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
